/**
 * @file SeoIndexingCard.tsx
 * @description «Сайт → SEO → Индексация» (phase19.16 Р1 → Р3в, v0.19.64;
 * SB45, SB145): «Показывать сайт в поиске» — включение сохраняется сразу,
 * выключение — через окно с последствиями и словом «закрыть»
 * (`CloseIndexingDialog`); адреса карты сайта и robots.txt со «Скопировать»
 * и «Открыть ↗». Пока сайт закрыт — красная полоса в карточке (и во всём
 * кабинете). Без своего адреса у магазина — пояснение, а не ссылка.
 */

import { useState } from "react";

import { NoticeStrip } from "../../ui/NoticeStrip";
import { useNotify } from "../../ui/NotifyProvider";
import { SectionCard } from "../../ui/SectionCard";
import { Switch } from "../../ui/switch";
import { CloseIndexingDialog } from "./CloseIndexingDialog";
import { SeoFieldMark } from "./SeoFieldMark";
import { SiteFileLink } from "./SiteFileLink";
import type { SeoCardProps } from "./types";
import { CLOSE_INDEXING_WORD } from "../../../lib/site-seo/close-word";
import { errorCode, seoErrorText } from "../../../lib/site-seo/seo-errors";
import { siteFileUrl } from "../../../lib/site-view/seo";

interface Props extends SeoCardProps {
  siteUrl: string | null;
}

export function SeoIndexingCard({ api, settings, onHistory, readOnly, siteUrl }: Props) {
  const notify = useNotify();
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  async function write(allowIndexing: boolean) {
    setBusy(true);
    try {
      await api.save(allowIndexing ? { allowIndexing } : { allowIndexing, confirm: CLOSE_INDEXING_WORD });
      setAsking(false);
      notify.success(allowIndexing ? "Сайт открыт для поисковиков. В поиске появится за дни или недели." : "Сайт закрыт от поисковиков.");
    } catch (err) {
      // 409 «изменено в другом окне» уже сказан тостом и перечитан хуком настроек.
      if (errorCode(err) !== "site_seo_version_conflict") notify.error(seoErrorText(err, "Не удалось изменить показ сайта в поиске."));
    } finally {
      setBusy(false);
    }
  }

  function toggle(on: boolean) {
    if (on) void write(true);
    else setAsking(true);
  }

  return (
    <SectionCard title="Индексация">
      {settings.allowIndexing ? null : (
        <NoticeStrip tone="error" title="Сайт закрыт от поисковиков">
          Яндекс и Google не показывают его в поиске.
        </NoticeStrip>
      )}
      <label className="flex min-h-12 cursor-pointer items-center gap-3">
        <Switch checked={settings.allowIndexing} disabled={readOnly || busy} onCheckedChange={toggle} />
        <span className="type-body text-on-surface">Показывать сайт в поиске</span>
      </label>
      <SeoFieldMark field="allowIndexing" settings={settings} onHistory={onHistory} readOnly={readOnly} />
      <p className="type-caption text-on-surface-variant">Отдельную страницу можно скрыть из поиска в её настройках в конструкторе.</p>
      {siteUrl ? (
        <>
          <SiteFileLink
            url={siteFileUrl(siteUrl, "sitemap.xml")}
            label="Карта сайта"
            note="Обновляется сама: товары, категории, страницы и статьи. Добавьте этот адрес в Яндекс Вебмастер и Google Search Console один раз."
          />
          <SiteFileLink url={siteFileUrl(siteUrl, "robots.txt")} label="Файл robots.txt" note="Платформа собирает его сама: служебные разделы закрыты, карта сайта указана." />
        </>
      ) : (
        <p className="type-body text-on-surface-variant">У магазина пока нет своего адреса — карта сайта появится после подключения домена.</p>
      )}
      <CloseIndexingDialog open={asking} busy={busy} onCancel={() => setAsking(false)} onConfirm={() => void write(false)} />
    </SectionCard>
  );
}
